/**
 * Public Q&A API — câu hỏi từ khán giả livestream + moderation flow.
 *
 * Endpoints (BE: app/api/v1/endpoints/courtrooms.py):
 *  - POST /courtrooms/public/{id}/qa     (no auth) — guest submit câu hỏi
 *  - GET  /courtrooms/public/{id}/qa     (no auth) — danh sách approved cho public watch
 *  - GET  /courtrooms/{id}/qa            (auth)   — moderation queue cho judge/BTC
 *  - POST /courtrooms/{id}/qa/{qid}/moderate (auth) — approve/reject
 */
import { apiSlice } from './apiSlice'

export interface PublicQuestion {
  id: string
  courtroom_id: string
  guest_id?: string | null
  guest_name?: string | null
  question: string
  status: 'pending' | 'approved' | 'rejected'
  created_at?: string
  updated_at?: string
  moderated_by?: string | null
  moderated_at?: string | null
}

export interface SubmitQuestionPayload {
  courtroomId: string
  question: string
  guest_name?: string
}

export interface ModerateQuestionPayload {
  courtroomId: string
  questionId: string
  action: 'approve' | 'reject'
  edited_question?: string
}

export const publicQaApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Public — không cần auth (apiSlice tự gắn token nếu có nhưng BE không yêu cầu).
    submitPublicQuestion: builder.mutation<
      { question_id: string; status: string },
      SubmitQuestionPayload
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/public/${courtroomId}/qa`,
        method: 'POST',
        body,
      }),
    }),
    listApprovedPublicQuestions: builder.query<PublicQuestion[], string>({
      query: (courtroomId) => `/courtrooms/public/${courtroomId}/qa`,
      providesTags: (res, err, courtroomId) => [
        { type: 'Courtroom' as const, id: `public-qa-${courtroomId}` },
      ],
    }),

    // Moderator (auth) — list queue tất cả / filter status
    listModerationQueue: builder.query<
      PublicQuestion[],
      { courtroomId: string; status?: string }
    >({
      query: ({ courtroomId, status }) => ({
        url: `/courtrooms/${courtroomId}/qa`,
        params: status ? { status } : undefined,
      }),
      providesTags: (res, err, { courtroomId }) => [
        { type: 'Courtroom' as const, id: `moderation-qa-${courtroomId}` },
      ],
    }),
    moderatePublicQuestion: builder.mutation<PublicQuestion, ModerateQuestionPayload>({
      query: ({ courtroomId, questionId, action, edited_question }) => ({
        url: `/courtrooms/${courtroomId}/qa/${questionId}/moderate`,
        method: 'POST',
        body: { action, edited_question },
      }),
      invalidatesTags: (res, err, { courtroomId }) => [
        { type: 'Courtroom' as const, id: `moderation-qa-${courtroomId}` },
        { type: 'Courtroom' as const, id: `public-qa-${courtroomId}` },
      ],
    }),
  }),
})

export const {
  useSubmitPublicQuestionMutation,
  useListApprovedPublicQuestionsQuery,
  useListModerationQueueQuery,
  useModeratePublicQuestionMutation,
} = publicQaApi
